import { createContext, type ReactNode, useCallback, useContext, useState } from "react"

export type PendingMessage = {
  text: string
  attachmentIds?: string[]
}

type PendingMessageContextType = {
  messages: PendingMessage[]
  setMessages: (m: PendingMessage[]) => void
  popMessage: () => PendingMessage | undefined
  addMessage: (m: PendingMessage) => void
}

const PendingMessageContext = createContext<PendingMessageContextType>({
  messages: [],
  setMessages: () => {},
  addMessage: () => {},
  popMessage: () => undefined
})

export function PendingMessageProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<PendingMessage[]>([])

  const popMessage = useCallback(() => {
    let popped: PendingMessage | undefined
    setMessages((prev) => {
      if (prev.length === 0) return prev
      const [first, ...rest] = prev
      popped = first
      return rest
    })
    return popped
  }, [])

  const addMessage = useCallback((msg: PendingMessage) => {
    setMessages((prev) => [...prev, msg])
  }, [])

  return (
    <PendingMessageContext.Provider value={{ messages, setMessages, addMessage, popMessage }}>
      {children}
    </PendingMessageContext.Provider>
  )
}

export const usePendingMessages = () => useContext(PendingMessageContext)
